import { Platform } from 'react-native';
import type { AlertButton } from 'react-native';

// Notification types
export type NotificationType = 'toast' | 'modal' | 'banner';

// Notification variants for styling
export type NotificationVariant = 'success' | 'error' | 'warning' | 'info';

// Enhanced notification options
export interface NotificationOptions {
	type?: NotificationType;
	duration?: number;
	position?: 'top' | 'bottom' | 'center';
	variant?: NotificationVariant;
	dismissible?: boolean;
	actions?: NotificationAction[];
}

// Notification action interface
export interface NotificationAction {
	label: string;
	onPress: () => void;
	style?: 'default' | 'success' | 'destructive' | 'cancel';
}

// Notification item interface
export interface NotificationItem {
	id: string;
	type: NotificationType;
	title?: string;
	message: string;
	variant: NotificationVariant;
	actions?: NotificationAction[];
	options: NotificationOptions;
	createdAt: number;
}

// Smart type detection algorithm
export function determineNotificationType(
	title: string,
	message: string,
	buttons?: AlertButton[],
	context?: string,
): NotificationType {
	const titleLower = title.toLowerCase();
	const messageLower = message.toLowerCase();

	// Success messages → Toast
	if (
		titleLower.includes('success')
		|| messageLower.includes('saved')
		|| messageLower.includes('created')
		|| messageLower.includes('updated')
		|| messageLower.includes('deleted')
		|| messageLower.includes('completed')
	) {
		return 'toast';
	}

	// Confirmation dialogs → Modal
	if (
		(buttons && buttons.length > 1)
		|| titleLower.includes('confirm')
		|| titleLower.includes('delete')
		|| titleLower.includes('remove')
		|| titleLower.includes('are you sure')
	) {
		return 'modal';
	}

	// Errors → Modal (blocking)
	if (
		titleLower.includes('error')
		|| titleLower.includes('failed')
		|| titleLower.includes('invalid')
		|| titleLower.includes('unauthorized')
		|| titleLower.includes('forbidden')
	) {
		return 'modal';
	}

	// Warnings → Banner (persistent)
	if (
		titleLower.includes('warning')
		|| titleLower.includes('caution')
		|| titleLower.includes('important')
		|| titleLower.includes('notice')
	) {
		return 'banner';
	}

	// Default → Toast for simple messages
	return 'toast';
}

// Determine notification variant based on content
export function determineNotificationVariant(
	title: string,
	message: string,
	type: NotificationType,
): NotificationVariant {
	const titleLower = title.toLowerCase();
	const messageLower = message.toLowerCase();

	// Success indicators
	if (
		titleLower.includes('success')
		|| messageLower.includes('saved')
		|| messageLower.includes('created')
		|| messageLower.includes('updated')
		|| messageLower.includes('completed')
	) {
		return 'success';
	}

	// Error indicators
	if (
		titleLower.includes('error')
		|| titleLower.includes('failed')
		|| titleLower.includes('invalid')
		|| titleLower.includes('unauthorized')
		|| titleLower.includes('forbidden')
	) {
		return 'error';
	}

	// Warning indicators
	if (
		titleLower.includes('warning')
		|| titleLower.includes('caution')
		|| titleLower.includes('important')
	) {
		return 'warning';
	}

	// Default to info
	return 'info';
}

// Convert AlertButton to NotificationAction
export function convertAlertButtonsToActions(buttons: AlertButton[]): NotificationAction[] {
	return buttons.map(button => ({
		label: button.text || 'OK',
		onPress: () => {
			button.onPress?.();
		},
		style: button.style || 'default',
	}));
}

// Generate unique notification ID
export function generateNotificationId(): string {
	return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Platform-specific default options
export function getPlatformDefaults(): Partial<NotificationOptions> {
	if (Platform.OS === 'web') {
		return {
			duration: 4000,
			position: 'top',
		};
	} else {
		return {
			duration: 5000,
			position: 'bottom',
		};
	}
}

// Notification queue management
export class NotificationQueue {
	private queue: NotificationItem[] = [];
	private maxSize: number = 10;

	add(notification: NotificationItem): void {
		// Keep only one modal at a time to avoid stacked blocking dialogs.
		if (notification.type === 'modal') {
			this.queue = this.queue.filter(existing => existing.type !== 'modal');
		}

		// Remove oldest notifications if queue is full
		if (this.queue.length >= this.maxSize) {
			this.queue.shift();
		}

		this.queue.push(notification);
	}

	remove(id: string): void {
		this.queue = this.queue.filter(notification => notification.id !== id);
	}

	getAll(): NotificationItem[] {
		return [...this.queue];
	}

	getByType(type: NotificationType): NotificationItem[] {
		return this.queue.filter(notification => notification.type === type);
	}

	clear(): void {
		this.queue = [];
	}

	getCount(): number {
		return this.queue.length;
	}
}

// Global notification queue instance
export const notificationQueue = new NotificationQueue();
